import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import RoomForm, { type RoomPayload } from "./RoomForm";
import { mastersApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency, formatDate, localToday } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import {
  DoorOpen, Edit2, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Download, CheckSquare,
  Square, Wrench, AlertTriangle, RefreshCw, CalendarPlus, ListChecks,
} from "lucide-react";
import { RoomFilters } from "./components";

type RoomSortField = 'newest' | 'name' | 'capacity' | 'rate' | 'status' | 'type';

const typeIcons: Record<string, string> = {
  "Banquet": "🏛️",
  "Open Air": "🌿",
  "Conference": "💼",
  "Accommodation": "🛏️"
};

const typeColors: Record<string, string> = {
  "Banquet": "bg-amber-50 text-amber-700",
  "Open Air": "bg-emerald-50 text-emerald-700",
  "Conference": "bg-indigo-50 text-indigo-700",
  "Accommodation": "bg-rose-50 text-rose-700"
};

/** A room booking as summarised by the rooms list */
interface BookingSummary {
  id: number;
  guest_name: string;
  event_name: string | null;
  check_in: string;
  check_out: string;
  status: string;
}

interface Room {
  id: number;
  name: string;
  type: string;
  capacity: number;
  rate: number | string;
  /** Manual flag: "available" (in service) or "maintenance" (out of service) */
  status: string;
  created_at?: string;
  /** Worked out by the server from the room's bookings */
  current_booking?: BookingSummary | null;
  next_booking?: BookingSummary | null;
  upcoming_bookings_count?: number;
}

/** What the room is doing today: from the bookings, except the manual maintenance flag */
type Occupancy = "maintenance" | "in" | "booked" | "free";

const occupancyOf = (r: Room): Occupancy =>
  r.status === "maintenance" ? "maintenance" : r.current_booking ? (r.current_booking.status === "checked_in" ? "in" : "booked") : "free";

const OCCUPANCY: Record<Occupancy, { label: string; cls: string; order: number }> = {
  free: { label: "Free today", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200", order: 0 },
  booked: { label: "Booked today", cls: "bg-amber-100 text-amber-800 border border-amber-200", order: 1 },
  in: { label: "Guest checked in", cls: "bg-blue-100 text-blue-700 border border-blue-200", order: 2 },
  maintenance: { label: "Maintenance", cls: "bg-stone-200 text-stone-700 border border-stone-300", order: 3 },
};

const fmtDay = (day?: string | null) => (day ? formatDate(`${day.slice(0, 10)}T00:00:00`) : "");

type Confirm =
  | { kind: "delete"; room: Room; error?: string }
  | { kind: "bulk-status"; status: "available" | "maintenance" }
  | { kind: "bulk-delete" };

export default function RoomsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === 'staff';
  const today = localToday();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [busy, setBusy] = useState(false);

  // Filter and sort state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  // Default: most recently created rooms first
  const [sortField, setSortField] = useState<RoomSortField>('newest');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Bulk actions state
  const [selectedRooms, setSelectedRooms] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRooms = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await mastersApi.rooms.list();
      setRooms(response.data?.data || response.data || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(apiErrorMessage(error, "Couldn't load rooms"));
    } finally {
      setLoading(false);
    }
  };

  // Handle sort
  const handleSort = (field: RoomSortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // "Newest" starts with the latest record on top; other fields start ascending
      setSortDirection(field === 'newest' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field: RoomSortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    return sortDirection === 'asc' ?
      <ArrowUp className="w-4 h-4 text-amber-600" /> :
      <ArrowDown className="w-4 h-4 text-amber-600" />;
  };

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openEdit = (room: Room) => {
    if (isStaff) return;
    setEditingRoom(room);
    setIsModalOpen(true);
  };
  const closeForm = () => { setIsModalOpen(false); setEditingRoom(null); };

  /** Throws on failure so the form stays open and shows the server's message */
  const handleFormSubmit = async (data: RoomPayload) => {
    if (editingRoom) {
      await mastersApi.rooms.update(editingRoom.id, data);
      showToast("Room updated", "success");
    } else {
      await mastersApi.rooms.create(data);
      showToast("Room added", "success");
    }
    closeForm();
    loadRooms(true);
  };

  const setRoomStatus = async (room: Room, status: "available" | "maintenance") => {
    try {
      await mastersApi.rooms.setStatus(room.id, status);
      setRooms(prev => prev.map(r => (r.id === room.id ? { ...r, status } : r)));
      showToast(status === "maintenance" ? `${room.name} is now under maintenance` : `${room.name} is back in service`, "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't change the room"), "error");
    }
  };

  const bookRoom = (room: Room) => navigate(`/room-bookings?new=1&room=${room.id}`);
  const seeBookings = (room: Room) => navigate(`/room-bookings?room=${room.id}`);

  const deleteRoom = async (room: Room) => {
    setBusy(true);
    try {
      await mastersApi.rooms.delete(room.id);
      showToast(`${room.name} deleted`, "success");
      setConfirm(null);
      setSelectedRooms(prev => { const s = new Set(prev); s.delete(room.id); return s; });
      loadRooms(true);
    } catch (error) {
      // e.g. the room has bookings: keep the dialog open with the reason and a way out
      setConfirm({ kind: "delete", room, error: apiErrorMessage(error, "Couldn't delete the room") });
    } finally {
      setBusy(false);
    }
  };

  const bulkSetStatus = async (status: "available" | "maintenance") => {
    setBusy(true);
    const ids = [...selectedRooms];
    const results = await Promise.allSettled(ids.map(id => mastersApi.rooms.setStatus(id, status)));
    const failed = results.filter(r => r.status === "rejected").length;
    setBusy(false);
    setConfirm(null);
    setSelectedRooms(new Set());
    loadRooms(true);
    if (failed) showToast(`${ids.length - failed} updated, ${failed} couldn't be changed`, "error");
    else showToast(`${ids.length} room${ids.length === 1 ? "" : "s"} updated`, "success");
  };

  const bulkDelete = async () => {
    setBusy(true);
    const ids = [...selectedRooms];
    const results = await Promise.allSettled(ids.map(id => mastersApi.rooms.delete(id)));
    const kept = results.filter(r => r.status === "rejected").length;
    setBusy(false);
    setConfirm(null);
    setSelectedRooms(new Set());
    loadRooms(true);
    if (kept) {
      showToast(`${ids.length - kept} deleted. ${kept} room${kept === 1 ? " has" : "s have"} bookings and ${kept === 1 ? "was" : "were"} kept (set ${kept === 1 ? "it" : "them"} to Maintenance instead).`, "error");
    } else {
      showToast(`${ids.length} room${ids.length === 1 ? "" : "s"} deleted`, "success");
    }
  };

  const menuItems = (room: Room): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ label: "See bookings", icon: ListChecks, onClick: () => seeBookings(room) }];
    if (isStaff) return items;
    items.unshift({ label: "Edit", icon: Edit2, onClick: () => openEdit(room) });
    items.push(room.status === "maintenance"
      ? { label: "Back in service", icon: DoorOpen, separated: true, onClick: () => setRoomStatus(room, "available") }
      : { label: "Set to maintenance", icon: Wrench, separated: true, onClick: () => setRoomStatus(room, "maintenance") });
    items.push({ label: "Delete…", icon: Trash2, danger: true, onClick: () => setConfirm({ kind: "delete", room }) });
    return items;
  };

  // ─── Filter / sort / paginate ────────────────────────────────────────────

  const roomTypes = useMemo(() => {
    const base = ["Banquet", "Open Air", "Conference", "Accommodation"];
    return [...new Set([...base, ...rooms.map(r => r.type).filter(Boolean)])];
  }, [rooms]);

  const filteredAndSortedRooms = useMemo(() => {
    let filtered = rooms;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(room =>
        room.name.toLowerCase().includes(searchLower) ||
        (room.type || "").toLowerCase().includes(searchLower) ||
        (room.current_booking?.guest_name || "").toLowerCase().includes(searchLower)
      );
    }

    // Status filter: free / booked today (from bookings) or maintenance (manual flag)
    if (statusFilter === 'free') {
      filtered = filtered.filter(room => occupancyOf(room) === 'free');
    } else if (statusFilter === 'booked') {
      filtered = filtered.filter(room => ['booked', 'in'].includes(occupancyOf(room)));
    } else if (statusFilter === 'maintenance') {
      filtered = filtered.filter(room => room.status === 'maintenance');
    }

    if (typeFilter) {
      filtered = filtered.filter(room => room.type === typeFilter);
    }

    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'newest':
          // Ascending = oldest first, so 'desc' yields byNewest order
          comparison = byNewest(b, a);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name, undefined, { numeric: true });
          break;
        case 'capacity':
          comparison = Number(a.capacity) - Number(b.capacity);
          break;
        case 'rate':
          comparison = Number(a.rate) - Number(b.rate);
          break;
        case 'status':
          comparison = OCCUPANCY[occupancyOf(a)].order - OCCUPANCY[occupancyOf(b)].order;
          break;
        case 'type':
          comparison = (a.type || "").localeCompare(b.type || "");
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [rooms, search, statusFilter, typeFilter, sortField, sortDirection]);

  const paginatedRooms = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRooms.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRooms, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedRooms.length / itemsPerPage);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRooms(new Set()); // Clear selection on filter change
  }, [search, statusFilter, typeFilter, sortField, sortDirection]);

  const showBulkActions = selectedRooms.size > 0;

  const toggleRoomSelection = (id: number) => {
    setSelectedRooms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      return newSet;
    });
  };

  const allOnPageSelected = paginatedRooms.length > 0 && paginatedRooms.every(r => selectedRooms.has(r.id));
  const toggleAllRooms = () => {
    setSelectedRooms(allOnPageSelected ? new Set() : new Set(paginatedRooms.map(r => r.id)));
  };

  // CSV export
  const handleExportCSV = () => {
    const roomsToExport = selectedRooms.size > 0
      ? rooms.filter(r => selectedRooms.has(r.id))
      : filteredAndSortedRooms;

    const headers = ['Room Name', 'Type', 'Capacity', 'Rate', 'Today', 'Guest today', 'Next booking', 'Upcoming bookings'];
    const rows = roomsToExport.map(room => [
      room.name,
      room.type,
      room.capacity,
      room.rate,
      OCCUPANCY[occupancyOf(room)].label,
      room.current_booking?.guest_name || '',
      room.next_booking ? `${room.next_booking.check_in} ${room.next_booking.guest_name}` : '',
      room.upcoming_bookings_count ?? 0,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rooms-export-${today}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (selectedRooms.size > 0) setSelectedRooms(new Set());
    showToast('CSV exported', 'success');
  };

  // ─── Rendering helpers ───────────────────────────────────────────────────

  const occupancyBadge = (room: Room) => {
    const o = OCCUPANCY[occupancyOf(room)];
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${o.cls}`}>{o.label}</span>;
  };

  /** One or two short lines: who is in today, and the next booking */
  const bookingLines = (room: Room) => {
    const cur = room.current_booking;
    const next = room.next_booking;
    return (
      <div className="text-xs text-stone-500 space-y-0.5">
        {cur && (
          <p className="truncate">
            <span className="text-stone-700">{cur.guest_name}</span>
            {cur.event_name ? ` · ${cur.event_name}` : ""} · until {fmtDay(cur.check_out)}
          </p>
        )}
        {next ? (
          <p className="truncate">
            Next: <span className="text-stone-700">{fmtDay(next.check_in)}</span> · {next.guest_name}
            {(room.upcoming_bookings_count || 0) > 1 ? ` (+${(room.upcoming_bookings_count || 0) - 1} more)` : ""}
          </p>
        ) : (
          !cur && <p>No upcoming bookings</p>
        )}
        {room.status === "maintenance" && cur && (
          <p className="text-red-600">Booked while under maintenance</p>
        )}
      </div>
    );
  };

  const bookButton = (room: Room, big = false) =>
    room.status === "maintenance" ? null : (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); bookRoom(room); }}
        className={`${big ? "h-10 px-3.5 text-sm" : "h-8 px-2.5 text-xs"} inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100 whitespace-nowrap`}
        aria-label={`Book ${room.name}`}
      >
        <CalendarPlus className="w-4 h-4" /> Book
      </button>
    );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="h-6 w-32 bg-stone-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-stone-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-stone-200 rounded-xl animate-pulse" />
        </div>
        <div className="h-16 bg-stone-100 rounded-xl border border-stone-200 mb-4 animate-pulse" />
        <div className="h-10 bg-stone-100 rounded-lg mb-4 animate-pulse" />
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" aria-label="Loading rooms">
          <div className="h-12 bg-stone-100 border-b border-stone-200 animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-b border-stone-100 animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load rooms</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button type="button" onClick={() => loadRooms()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const sortButton = (field: RoomSortField, label: string) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 min-h-9 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        sortField === field ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
      }`}
    >
      {label} {getSortIcon(field)}
    </button>
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-stone-800">Rooms</h1>
          <p className="text-sm text-stone-500">Who is in each room today and the next booking, from Room Bookings</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={filteredAndSortedRooms.length === 0}
            className="px-4 py-2 min-h-10 rounded-xl bg-stone-100 text-stone-700 font-semibold text-sm flex items-center gap-2 hover:bg-stone-200 transition-colors disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {!isStaff && (
            <button
              type="button"
              onClick={() => { setEditingRoom(null); setIsModalOpen(true); }}
              className="px-4 py-2 min-h-10 rounded-xl gold-gradient text-white font-semibold text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Room
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <RoomFilters
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          onApply={() => {}} // Filters are applied reactively
          types={roomTypes}
        />
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && !isStaff && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-amber-800">{selectedRooms.size} room{selectedRooms.size === 1 ? "" : "s"} selected</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setConfirm({ kind: "bulk-status", status: "available" })}
              className="flex items-center gap-2 min-h-10 px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-medium hover:bg-emerald-200 transition-colors"
            >
              <DoorOpen className="w-4 h-4" />
              Set Available
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ kind: "bulk-status", status: "maintenance" })}
              className="flex items-center gap-2 min-h-10 px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              <Wrench className="w-4 h-4" />
              Set Maintenance
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ kind: "bulk-delete" })}
              className="flex items-center gap-2 min-h-10 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-sm font-medium hover:bg-rose-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedRooms(new Set())}
              className="min-h-10 px-3 py-2 rounded-lg bg-white text-stone-600 text-sm font-medium hover:bg-stone-100 transition-colors border border-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sort and View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-stone-500">Sort by:</span>
          <button
            type="button"
            onClick={() => handleSort('newest')}
            className={`flex items-center gap-1 min-h-9 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'newest' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            {sortField === 'newest' && sortDirection === 'asc' ? 'Oldest' : 'Newest'} {getSortIcon('newest')}
          </button>
          {sortButton('name', 'Name')}
          {sortButton('type', 'Type')}
          {sortButton('capacity', 'Capacity')}
          {sortButton('rate', 'Rate')}
          {sortButton('status', 'Today')}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Rooms per page"
            className="text-xs border border-stone-300 rounded-lg px-2 min-h-9 bg-white"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {/* Rooms - mobile cards */}
      {paginatedRooms.length > 0 && (
        <div className="md:hidden bg-white rounded-xl border border-stone-200 overflow-hidden">
          {!isStaff && (
            <div className="flex items-center justify-between gap-3 px-4 py-2 bg-stone-50 border-b border-stone-200">
              <button
                type="button"
                onClick={toggleAllRooms}
                className="h-10 flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-amber-600 transition-colors"
                aria-label={allOnPageSelected ? "Deselect all rooms on this page" : "Select all rooms on this page"}
              >
                {allOnPageSelected ? <CheckSquare className="w-5 h-5 text-amber-600" /> : <Square className="w-5 h-5 text-stone-400" />}
                Select all
              </button>
              {selectedRooms.size > 0 && (
                <span className="text-xs text-stone-500">{selectedRooms.size} selected</span>
              )}
            </div>
          )}
          <div className="divide-y divide-stone-100">
            {paginatedRooms.map((room) => {
              const isSelected = selectedRooms.has(room.id);
              return (
                <div key={room.id} className={`p-4 ${isSelected ? 'bg-amber-50' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      {!isStaff && (
                        <button
                          type="button"
                          onClick={() => toggleRoomSelection(room.id)}
                          className="h-10 w-10 -ml-2 -mt-2 shrink-0 flex items-center justify-center rounded-lg hover:text-amber-600 transition-colors"
                          aria-label={isSelected ? `Deselect ${room.name}` : `Select ${room.name}`}
                          aria-pressed={isSelected}
                        >
                          {isSelected ? <CheckSquare className="w-5 h-5 text-amber-600" /> : <Square className="w-5 h-5 text-stone-400" />}
                        </button>
                      )}
                      <button type="button" className="min-w-0 text-left" onClick={() => (isStaff ? seeBookings(room) : openEdit(room))}>
                        <p className="font-semibold text-stone-800 truncate">{room.name}</p>
                        <p className="text-xs text-stone-500 mt-0.5 truncate">
                          {typeIcons[room.type] || '🏠'} {room.type} · {room.capacity} guests
                        </p>
                      </button>
                    </div>
                    {occupancyBadge(room)}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
                    <div className="col-span-2 min-w-0">
                      <p className="text-xs text-stone-400">Bookings</p>
                      {bookingLines(room)}
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">Rate</p>
                      <p className="font-semibold text-stone-700">{formatCurrency(room.rate)}<span className="text-xs font-normal text-stone-400"> /day</span></p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {bookButton(room, true)}
                    <ActionMenu items={menuItems(room)} label={`More actions for ${room.name}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rooms Table */}
      {paginatedRooms.length > 0 && (
        <div className="hidden md:block bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  {!isStaff && (
                    <th className="text-center px-4 py-3 w-10">
                      <button
                        type="button"
                        onClick={toggleAllRooms}
                        className="hover:text-amber-600 transition-colors text-stone-600"
                        aria-label={allOnPageSelected ? "Deselect all rooms on this page" : "Select all rooms on this page"}
                      >
                        {allOnPageSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Room</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Capacity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Rate</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Today</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Bookings</th>
                  <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {paginatedRooms.map((room) => {
                  const isSelected = selectedRooms.has(room.id);
                  return (
                    <tr
                      key={room.id}
                      onClick={() => (isStaff ? seeBookings(room) : openEdit(room))}
                      className={`cursor-pointer hover:bg-amber-50/40 transition-colors ${isSelected ? 'bg-amber-50' : ''}`}
                    >
                      {!isStaff && (
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => toggleRoomSelection(room.id)}
                            className="h-8 w-8 inline-flex items-center justify-center hover:text-amber-600 transition-colors"
                            aria-label={isSelected ? `Deselect ${room.name}` : `Select ${room.name}`}
                            aria-pressed={isSelected}
                          >
                            {isSelected ? <CheckSquare className="w-4 h-4 text-amber-600" /> : <Square className="w-4 h-4 text-stone-400" />}
                          </button>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <p className="font-medium text-stone-800">{room.name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{typeIcons[room.type] || '🏠'}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeColors[room.type] || 'bg-stone-100 text-stone-600'}`}>{room.type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-stone-600">{room.capacity} guests</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-stone-700">{formatCurrency(room.rate)}</p>
                        <p className="text-xs text-stone-400">/day</p>
                      </td>
                      <td className="px-4 py-3">{occupancyBadge(room)}</td>
                      <td className="px-4 py-3 max-w-[280px]">{bookingLines(room)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {bookButton(room)}
                          <ActionMenu items={menuItems(room)} label={`More actions for ${room.name}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {paginatedRooms.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <DoorOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">
            {search || statusFilter || typeFilter ? 'No rooms match your filters' : 'No rooms yet'}
          </p>
          {(search || statusFilter || typeFilter) ? (
            <button
              type="button"
              onClick={() => { setSearch(""); setStatusFilter(""); setTypeFilter(""); }}
              className="mt-3 min-h-10 text-sm font-semibold text-amber-700 hover:underline"
            >
              Clear filters
            </button>
          ) : !isStaff && (
            <button
              type="button"
              onClick={() => { setEditingRoom(null); setIsModalOpen(true); }}
              className="mt-3 h-10 px-4 rounded-xl gold-gradient text-white text-sm font-medium"
            >
              Add First Room
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
          <p className="text-sm text-stone-500 text-center sm:text-left">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedRooms.length)} of {filteredAndSortedRooms.length} rooms
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              aria-label="Previous page"
              className="h-10 w-10 flex items-center justify-center rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex flex-wrap items-center justify-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    type="button"
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    aria-current={currentPage === pageNum ? "page" : undefined}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-amber-500 text-white'
                        : 'border border-stone-300 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              aria-label="Next page"
              className="h-10 w-10 flex items-center justify-center rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Room Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeForm}
        title={editingRoom ? "Edit Room" : "Add New Room"}
        size="md"
      >
        {isModalOpen && (
          <RoomForm
            key={editingRoom?.id ?? "new"}
            initialData={editingRoom ? {
              name: editingRoom.name,
              type: editingRoom.type,
              capacity: String(editingRoom.capacity ?? ""),
              rate: String(Number(editingRoom.rate) || 0),
              status: editingRoom.status,
            } : undefined}
            onSubmit={handleFormSubmit}
            onCancel={closeForm}
          />
        )}
      </Modal>

      {/* Confirmations (instead of browser pop-ups) */}
      {confirm && (
        <Modal
          isOpen={!!confirm}
          onClose={() => !busy && setConfirm(null)}
          title={
            confirm.kind === "delete" ? `Delete ${confirm.room.name}?`
              : confirm.kind === "bulk-delete" ? `Delete ${selectedRooms.size} room${selectedRooms.size === 1 ? "" : "s"}?`
                : confirm.status === "maintenance" ? `Set ${selectedRooms.size} room${selectedRooms.size === 1 ? "" : "s"} to maintenance?`
                  : `Set ${selectedRooms.size} room${selectedRooms.size === 1 ? "" : "s"} to available?`
          }
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setConfirm(null)} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                {confirm.kind === "delete" && confirm.error ? "Close" : "Cancel"}
              </button>
              {confirm.kind === "delete" && confirm.error && confirm.room.status !== "maintenance" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => { const room = confirm.room; setConfirm(null); await setRoomStatus(room, "maintenance"); }}
                  className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50"
                >
                  Set to maintenance instead
                </button>
              )}
              {confirm.kind === "delete" && !confirm.error && (
                <button type="button" onClick={() => deleteRoom(confirm.room)} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                  {busy ? "Deleting…" : "Delete room"}
                </button>
              )}
              {confirm.kind === "bulk-delete" && (
                <button type="button" onClick={bulkDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                  {busy ? "Deleting…" : "Delete rooms"}
                </button>
              )}
              {confirm.kind === "bulk-status" && (
                <button type="button" onClick={() => bulkSetStatus(confirm.status)} disabled={busy} className="h-11 px-4 rounded-xl gold-gradient text-white text-sm font-semibold disabled:opacity-50">
                  {busy ? "Saving…" : confirm.status === "maintenance" ? "Set to maintenance" : "Set available"}
                </button>
              )}
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            {confirm.kind === "delete" && (confirm.error ? (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-800 px-3 py-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {confirm.error}
              </p>
            ) : (
              <p>This removes the room for good. Rooms that have bookings can't be deleted; set them to maintenance instead.</p>
            ))}
            {confirm.kind === "bulk-delete" && (
              <p>This removes the selected rooms for good. Any room that has bookings will be kept.</p>
            )}
            {confirm.kind === "bulk-status" && (
              <p>
                {confirm.status === "maintenance"
                  ? "These rooms will be out of service and can't be booked until you set them back to available. Existing bookings stay as they are."
                  : "These rooms will be in service and can be booked again."}
              </p>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
